import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Store, ArrowLeft, Eye, EyeOff, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { customerAuthAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function CustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });

  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || '/';

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await customerAuthAPI.login(formData);
      const { token, user_id, role, customer_id } = response.data;
      login({ user_id, role, customer_id }, token);
      toast.success('Welcome back!');
      navigate(redirectUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!otpEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await customerAuthAPI.requestOTP(otpEmail);
      setOtpSent(true);
      toast.success('OTP sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const response = await customerAuthAPI.verifyOTP(otpEmail, otp);
      const { token, user_id, role, customer_id } = response.data;
      login({ user_id, role, customer_id }, token);
      toast.success('Welcome back!');
      navigate(redirectUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Determine back link - use store context if available
  const getBackLink = () => {
    if (redirectUrl.includes('/shop/')) {
      // Extract shop slug from redirect URL
      const match = redirectUrl.match(/\/shop\/([^/]+)/);
      if (match) return `/shop/${match[1]}`;
    }
    const lastStore = localStorage.getItem('lastVisitedStore');
    return lastStore ? `/shop/${lastStore}` : '/';
  };

  const backLink = getBackLink();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link 
          to={backLink} 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLink === '/' ? 'Back to home' : 'Back to store'}
        </Link>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Store className="h-8 w-8 text-primary" />
              <span className="font-heading text-xl font-bold">My Market Place</span>
            </div>
            <CardTitle className="font-heading text-2xl">Customer Login</CardTitle>
            <CardDescription>
              Sign in to track your orders
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="password" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="password" className="gap-2">
                  <Phone className="h-4 w-4" /> Password
                </TabsTrigger>
                <TabsTrigger value="otp" className="gap-2">
                  <Mail className="h-4 w-4" /> Email OTP
                </TabsTrigger>
              </TabsList>

              <TabsContent value="password">
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="identifier">Email or Phone</Label>
                    <Input
                      id="identifier"
                      name="identifier"
                      value={formData.identifier}
                      onChange={handleInputChange}
                      placeholder="your@email.com or 9876543210"
                      required
                      data-testid="customer-identifier-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        required
                        data-testid="customer-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full rounded-full" 
                    disabled={loading}
                    data-testid="customer-login-btn"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="otp">
                {!otpSent ? (
                  <form onSubmit={handleRequestOTP} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp-email">Email Address</Label>
                      <Input
                        id="otp-email"
                        type="email"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        data-testid="otp-email-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full rounded-full" 
                      disabled={loading}
                      data-testid="request-otp-btn"
                    >
                      {loading ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp">Enter OTP</Label>
                      <Input
                        id="otp"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        className="text-center text-2xl tracking-widest"
                        data-testid="otp-input"
                      />
                      <p className="text-xs text-muted-foreground text-center">
                        OTP sent to {otpEmail}
                      </p>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full rounded-full" 
                      disabled={loading}
                      data-testid="verify-otp-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost"
                      className="w-full"
                      onClick={() => setOtpSent(false)}
                    >
                      Change Email
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to={`/customer/signup?redirect=${encodeURIComponent(redirectUrl)}`} className="text-primary hover:underline">
                Sign up
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
